<template>
  <!-- Auth UI — topbar position -->
  <!-- Guest: Login + Register buttons -->
  <!-- Authenticated: dropdown with Profile + Logout -->
  <div class="user-menu" :class="{ 'is-open': menuOpen }">

    <!-- ===== HYDRATING STATE ===== -->
    <div v-if="!isHydrated" class="user-menu-hydrating">
      <span class="user-menu-hydrating-pill">Loading session...</span>
    </div>

    <!-- ===== GUEST STATE ===== -->
    <div v-else-if="!isAuthenticated" class="user-menu-guest">
      <router-link class="auth-btn auth-btn--ghost" to="/login">Login</router-link>
      <router-link class="auth-btn auth-btn--primary" :to="{ path: '/login', query: { tab: 'register' } }">Register</router-link>
    </div>

    <!-- ===== AUTHENTICATED STATE ===== -->
    <div v-else class="user-menu-auth">
      <button class="user-avatar-btn" @click="toggleMenu">
        <span class="user-avatar-icon">{{ avatarLetter }}</span>
        <span class="user-avatar-label">{{ displayName }}</span>
        <span class="user-avatar-caret">▾</span>
      </button>

      <!-- Dropdown -->
      <div v-if="menuOpen" class="user-dropdown" @click.stop>
        <div class="user-dropdown-header">
          <span class="user-dropdown-email">{{ user?.email || 'user' }}</span>
          <span class="user-dropdown-role">{{ user?.role || 'user' }}</span>
          <span v-if="user?.tenant_id" class="user-dropdown-tenant">{{ user.tenant_id }}</span>
        </div>

        <nav class="user-dropdown-nav">
          <router-link class="dropdown-item" to="/profile" @click="closeMenu">
            <span class="dropdown-icon">👤</span>Profile
          </router-link>
          <router-link class="dropdown-item" to="/company/staff" @click="closeMenu">
            <span class="dropdown-icon">👥</span>Staff
          </router-link>
          <router-link class="dropdown-item" to="/company/sites" @click="closeMenu">
            <span class="dropdown-icon">🏢</span>Sites
          </router-link>
          <!-- Admin Dashboard — super_admin only; page file exists -->
          <router-link
            v-if="isSuperAdmin"
            class="dropdown-item dropdown-item--admin"
            to="/admin/dashboard"
            @click="closeMenu"
          >
            <span class="dropdown-icon">⚙️</span>Admin Dashboard
          </router-link>
        </nav>

        <div class="user-dropdown-footer">
          <button class="dropdown-item dropdown-item--danger" @click="handleLogout">
            <span class="dropdown-icon">↩</span>Logout
          </button>
        </div>

        <!-- Dev debug summary (debug mode only) -->
        <div v-if="debugEnabled" class="dropdown-debug">
          <span class="debug-badge">AUTH_TOKEN_PRESENT</span>
          <span class="debug-badge">role: {{ user?.role }}</span>
          <span class="debug-badge">tenant: {{ user?.tenant_id }}</span>
        </div>
      </div>

      <!-- Click-outside overlay -->
      <div v-if="menuOpen" class="dropdown-overlay" @click="closeMenu" />
    </div>

    <!-- Dev: token missing warning (debug mode only) -->
    <div v-if="isHydrated && !isAuthenticated && debugEnabled" class="auth-debug-badge">
      AUTH_TOKEN_MISSING
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '../../composables/useAuth/useAuth.js';
import { DASHBOARD_DEBUG, debugLog } from '../../utils/debugGate.js';

const router = useRouter();
const { isAuthenticated, isHydrated, user, logout, getSessionSnapshot } = useAuth();
const menuOpen = ref(false);

const debugEnabled = DASHBOARD_DEBUG;

// English: isSuperAdmin gates Admin Dashboard dropdown link.
//          Only AdminDashboardPage.vue exists — other admin sub-pages are not shown.
// Thai: isSuperAdmin ควบคุม link Admin Dashboard ใน dropdown
//       มีเฉพาะ AdminDashboardPage.vue — admin sub-pages อื่น ไม่แสดง
const isSuperAdmin = computed(() => user.value?.role === 'super_admin');

const displayName = computed(() => {
  if (!user.value) return '';
  return user.value.email?.split('@')[0] || 'user';
});

const avatarLetter = computed(() => {
  return displayName.value?.charAt(0)?.toUpperCase() || 'U';
});

function toggleMenu() {
  if (!isHydrated.value || !isAuthenticated.value) return;
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

async function handleLogout() {
  closeMenu();
  logout();

  debugLog('[UserMenu][debug]', {
    action: 'logout',
    session: getSessionSnapshot(),
    message_en: 'User logged out from UserMenu.',
    message_th: 'ผู้ใช้ logout ผ่าน UserMenu แล้ว',
  });

  await router.replace({ name: 'Login' });
}

onMounted(() => {
  if (!isHydrated.value) {
    debugLog('[UserMenu][debug]', {
      event: 'AUTH_UI_RENDER_HYDRATING',
      session: getSessionSnapshot(),
      message_en: 'Auth UI rendered in hydrating state. Waiting before deciding between guest and authenticated menu.',
      message_th: 'Auth UI แสดงผลในสถานะกำลัง hydrate และกำลังรอก่อนตัดสินใจระหว่าง guest กับ authenticated menu',
    });
    return;
  }

  if (isAuthenticated.value) {
    debugLog('[UserMenu][debug]', {
      event: 'AUTH_UI_RENDER_AUTHENTICATED',
      session: getSessionSnapshot(),
      user_id:   user.value?.user_id,
      tenant_id: user.value?.tenant_id,
      role:      user.value?.role,
      message_en: 'Auth UI rendered in authenticated state. Showing user menu with Profile + Logout.',
      message_th: 'Auth UI แสดงผลในสถานะ authenticated แสดงเมนู Profile + Logout',
    });
  } else {
    debugLog('[UserMenu][debug]', {
      event: 'AUTH_UI_RENDER_GUEST',
      session: getSessionSnapshot(),
      message_en: 'Auth UI rendered in guest state. Showing Login + Register buttons.',
      message_th: 'Auth UI แสดงผลในสถานะ guest แสดงปุ่ม Login + Register',
    });
  }
});
</script>

<style scoped>
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-menu-hydrating {
  display: flex;
  align-items: center;
}

.user-menu-hydrating-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #bcccdc;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  white-space: nowrap;
}

/* ---- GUEST ---- */
.user-menu-guest {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auth-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}

.auth-btn--ghost {
  background: transparent;
  border: 1px solid rgba(255,255,255,0.3);
  color: #bcccdc;
}

.auth-btn--ghost:hover {
  background: rgba(255,255,255,0.08);
  color: #fff;
}

.auth-btn--primary {
  background: #3b82f6;
  border: 1px solid #3b82f6;
  color: #fff;
}

.auth-btn--primary:hover {
  background: #2563eb;
}

/* ---- AUTH ---- */
.user-avatar-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s;
}

.user-avatar-btn:hover {
  background: rgba(255,255,255,0.14);
}

.user-avatar-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #3b82f6;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar-caret {
  font-size: 10px;
  color: #94a3b8;
}

/* ---- DROPDOWN ---- */
.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  background: #1a1d27;
  border: 1px solid #2a2d3a;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4);
  z-index: 9999;
  overflow: hidden;
}

.user-dropdown-header {
  padding: 12px 14px;
  border-bottom: 1px solid #2a2d3a;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-dropdown-email {
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-role,
.user-dropdown-tenant {
  color: #64748b;
  font-size: 11px;
}

.user-dropdown-nav,
.user-dropdown-footer {
  padding: 6px;
}

.user-dropdown-footer {
  border-top: 1px solid #2a2d3a;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  text-align: left;
}

.dropdown-item:hover {
  background: rgba(255,255,255,0.06);
  color: #e2e8f0;
}

.dropdown-item--danger:hover {
  background: rgba(239,68,68,0.1);
  color: #f87171;
}

.dropdown-item--admin {
  color: #a78bfa;
}

.dropdown-item--admin:hover {
  background: rgba(167,139,250,0.12);
  color: #c084fc;
}

.dropdown-icon {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

/* ---- OVERLAY ---- */
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 9998;
}

/* ---- DEBUG ---- */
.auth-debug-badge {
  font-size: 10px;
  font-weight: 700;
  color: #f97316;
  background: rgba(249,115,22,0.1);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(249,115,22,0.3);
}

.dropdown-debug {
  padding: 8px 10px;
  border-top: 1px solid #2a2d3a;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.debug-badge {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  background: #0f1117;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #2a2d3a;
}
</style>
